import { create } from "zustand";

import { queryClient } from "@/lib/queryClient";
import { customerTokenService } from "@/services/customer-auth";
import type { CustomerAuthResponse } from "@/types/customer-auth.types";


// ============================================
// Customer User Type (simplified from API response)
// ============================================

export interface CustomerUser {
  id: string;
  email: string;
  full_name: string;
  phone?: string | null;
  profile_image_url?: string | null;
}

// ============================================
// Customer Auth Store
// ============================================

interface CustomerAuthStore {
  user: CustomerUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (data: CustomerAuthResponse) => void;
  logout: () => void;
  setUser: (user: CustomerUser | null) => void;
  initAuth: () => void;
}

export const useCustomerAuthStore = create<CustomerAuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,

  /**
   * Login - save tokens and user data to localStorage
   */
  login: (data: CustomerAuthResponse) => {
    customerTokenService.saveAuthData(data);
    set({
      user: data.user,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  /**
   * Logout - clear all auth data
   */
  logout: () => {
    customerTokenService.clearAuth();
    queryClient.clear();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  /**
   * Set user data
   */
  setUser: (user: CustomerUser | null) => {
    if (user) {
      customerTokenService.setUser(user);
    }
    set({
      user,
      isAuthenticated: !!user,
      isLoading: false,
    });
  },

  /**
   * Initialize auth state from localStorage on app load
   */
  initAuth: () => {
    const token = customerTokenService.getAccessToken();
    const user = customerTokenService.getUser();
    const type = customerTokenService.getType();

    // Only set authenticated if we have token AND type is customer
    if (token && type === "customer" && user) {
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } else {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },
}));
